/**
 * src/shell/portal/components/ChannelInviteModal.tsx
 *
 * Cryptographic channel invite link generator.
 * Issues signed capability tokens (View-Only vs Member) with usage caps and URL hash copying.
 */

import React, { useState } from 'react';
import {
  SocialChannel,
  ChannelInviteToken,
} from '../../../domain/social/channel-contracts';
import { channelManager } from '../../../domain/social/channel-manager';
import { X, Copy, Check, Eye, UserPlus, QrCode } from 'lucide-react';

export interface ChannelInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  channel: SocialChannel;
  creatorPersonaId: string;
}

export const ChannelInviteModal: React.FC<ChannelInviteModalProps> = ({
  isOpen,
  onClose,
  channel,
  creatorPersonaId,
}) => {
  const [role, setRole] = useState<'view_only' | 'member'>('member');
  const [usageCap, setUsageCap] = useState<number>(0); // 0 = unlimited
  const [durationDays, setDurationDays] = useState<number>(7);
  const [generatedInvite, setGeneratedInvite] = useState<ChannelInviteToken | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleGenerate = () => {
    const invite = channelManager.createInviteToken({
      channelId: channel.id,
      capabilities: {
        canView: true,
        canPost: role === 'member',
      },
      maxUses: usageCap,
      durationDays,
      creatorPersonaId,
    });
    setGeneratedInvite(invite);
  };

  const inviteUrl = generatedInvite
    ? `https://herx.org/#/invite/${generatedInvite.token}`
    : '';

  const handleCopy = () => {
    if (!inviteUrl) return;
    navigator.clipboard?.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#0c101a',
          border: '1px solid #1e293b',
          borderRadius: 16,
          width: '100%',
          maxWidth: 460,
          color: '#f8fafc',
          boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Invite to #{channel.slug}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Generate a signed cryptographic invite token</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Role selector */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
              Member Access Level
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div
                onClick={() => setRole('member')}
                style={{
                  background: role === 'member' ? 'rgba(56, 189, 248, 0.15)' : '#070a10',
                  border: role === 'member' ? '1px solid #38bdf8' : '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 10,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <UserPlus size={16} color={role === 'member' ? '#38bdf8' : '#64748b'} style={{ margin: '0 auto 4px auto' }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: role === 'member' ? '#f8fafc' : '#94a3b8' }}>Full Member</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Read & post access</div>
              </div>

              <div
                onClick={() => setRole('view_only')}
                style={{
                  background: role === 'view_only' ? 'rgba(56, 189, 248, 0.15)' : '#070a10',
                  border: role === 'view_only' ? '1px solid #38bdf8' : '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 10,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <Eye size={16} color={role === 'view_only' ? '#38bdf8' : '#64748b'} style={{ margin: '0 auto 4px auto' }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: role === 'view_only' ? '#f8fafc' : '#94a3b8' }}>View-Only</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Read-only feed</div>
              </div>
            </div>
          </div>

          {/* Usage Limit & Duration */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
                Usage Limit
              </label>
              <select
                value={usageCap}
                onChange={(e) => setUsageCap(Number(e.target.value))}
                style={{
                  width: '100%',
                  background: '#070a10',
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  padding: '8px 10px',
                  color: '#ffffff',
                  fontSize: 12,
                  outline: 'none',
                }}
              >
                <option value={0}>Unlimited</option>
                <option value={1}>Single-Use (1 person)</option>
                <option value={5}>5 Uses</option>
                <option value={25}>25 Uses</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
                Link Expiration
              </label>
              <select
                value={durationDays}
                onChange={(e) => setDurationDays(Number(e.target.value))}
                style={{
                  width: '100%',
                  background: '#070a10',
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  padding: '8px 10px',
                  color: '#ffffff',
                  fontSize: 12,
                  outline: 'none',
                }}
              >
                <option value={1}>24 Hours</option>
                <option value={7}>7 Days</option>
                <option value={30}>30 Days</option>
              </select>
            </div>
          </div>

          {!generatedInvite ? (
            <button
              type="button"
              onClick={handleGenerate}
              style={{
                width: '100%',
                marginTop: 8,
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                border: 'none',
                borderRadius: 8,
                padding: '10px 14px',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Generate Cryptographic Link
            </button>
          ) : (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div
                style={{
                  background: '#070a10',
                  border: '1px solid #0284c7',
                  borderRadius: 8,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, color: '#38bdf8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {inviteUrl}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  style={{
                    background: copied ? '#10b981' : '#1e293b',
                    border: 'none',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>
                Ed25519 signature verified • Expiring in {durationDays} days
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
